import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PetTypeIds } from '@spike/model';
import clsx from 'clsx';
import CounterField from 'components/UI/V3/CounterField/CounterField';
import { useMarketplace } from 'hooks';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { RuleSizeData } from './RuleSize';
import styles from './RuleSizeItem.module.scss';
import GroupSelector from './GroupSelector/GroupSelector';

interface RuleSizeItemProps {
    ruleSize: RuleSizeData;
    roomPetTypeIds: Array<string>;
    usedRules: Array<RuleSizeData>;
    onChange: (rule: RuleSizeData) => void;
    onDelete?: () => void;
    onUsedAllSizes?: (value: boolean) => void;
    error?: boolean;
    className?: string;
}

export const RuleSizeItem: FunctionComponent<RuleSizeItemProps> = props => {
    const marketplace = useMarketplace();

    const sizeOptions = useMemo(
        () =>
            [
                ...marketplace.petTypes.dogs.sizes.map(size => ({
                    id: size.element.id,
                    label: `${size.element.name}`,
                    groupId: PetTypeIds.DOGS,
                    groupLabel: 'DOGS'
                })),
                ...(marketplace.petTypes.cats.sizes ?? []).map(size => ({
                    id: size.element.id,
                    label: `${size.element.name}`,
                    groupId: PetTypeIds.CATS,
                    groupLabel: 'CATS'
                })),
                ...(marketplace.petTypes.exotics.sizes ?? []).map(size => ({
                    id: size.element.id,
                    label: `${size.element.name}`,
                    groupId: PetTypeIds.EXOTICS,
                    groupLabel: 'EXOTICS'
                }))
            ].filter(option => props.roomPetTypeIds.includes(option.groupId)),
        [
            marketplace.petTypes.dogs.sizes,
            marketplace.petTypes.cats.sizes,
            marketplace.petTypes.exotics.sizes,
            props.roomPetTypeIds
        ]
    );

    useEffect(() => {
        const sizeOptionsFiltered = sizeOptions.filter(option => {
            const isUsed = props.usedRules.some(
                usedRule => usedRule.sizeId === option.id && usedRule.petTypeId === option.groupId
            );
            return !isUsed;
        });
        if (sizeOptionsFiltered.length === 1) {
            props.onUsedAllSizes?.(true);
        } else {
            props.onUsedAllSizes?.(false);
        }
    }, [sizeOptions, props.usedRules, props.onUsedAllSizes]);

    return (
        <div>
            <div className={clsx(styles.container, props.className)}>
                <div className={clsx({ [styles.error]: props.error })}>
                    <CounterField
                        value={props.ruleSize.quantity}
                        onChange={quantity => quantity && props.onChange({ ...props.ruleSize, quantity })}
                        minLimit={1}
                        maxLimit={999}
                        hasBorder={true}
                        className={styles.counter}
                    />
                </div>
                <GroupSelector
                    placeholder="Select Size"
                    options={sizeOptions.filter(option => {
                        const isUsed = props.usedRules.some(
                            usedRule => usedRule.sizeId === option.id && usedRule.petTypeId === option.groupId
                        );
                        return !isUsed;
                    })}
                    error={props.error}
                    selected={sizeOptions.find(
                        o => o.id === props.ruleSize.sizeId && o.groupId === props.ruleSize.petTypeId
                    )}
                    onSelect={option =>
                        props.onChange({ ...props.ruleSize, petTypeId: option.groupId, sizeId: option.id })
                    }
                />
                {props.onDelete && (
                    <FontAwesomeIcon
                        icon={faTimes}
                        onClick={props.onDelete}
                        className={styles.deleteIcon}
                    />
                )}
            </div>
            {props.error && (
                <span className={clsx({ [styles.errorWarning]: props.error })}>
                    Each pet size can only be defined once per rule. Remove or change this duplicate size.
                </span>
            )}
        </div>
    );
};

export default RuleSizeItem;
